import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Wishlist.css';
import WishPop from './Wishpop';

function Wishlist() {
	const [wishlist, setWishlist] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showWishPop, setShowWishPop] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [currentPage, setCurrentPage] = useState(1); // ⭐ 추가
	const itemsPerPage = 20; // ⭐ 4*5 = 20개
	const navigate = useNavigate();

	const getToken = () => {
		return localStorage.getItem('access_token');
	};

	const isLoggedIn = () => {
		return !!getToken();
	};

	useEffect(() => {
		if (!isLoggedIn()) {
			alert('로그인이 필요한 서비스입니다.');
			navigate('/login');
			return;
		}
		fetchWishlist();

		window.addEventListener('wishlistUpdated', fetchWishlist);
		return () => {
			window.removeEventListener('wishlistUpdated', fetchWishlist);
		};
	}, [navigate]);

	const fetchWishlist = async () => {
		setLoading(true);
		try {
			const token = getToken();
			const response = await axios.get(
				'https://api.weartoday.kr/api/products/wishlist/',
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);

			console.log('✅ 찜목록 API 응답:', response.data);

			if (Array.isArray(response.data)) {
				setWishlist(response.data);
			} else if (response.data.results && Array.isArray(response.data.results)) {
				setWishlist(response.data.results);
			} else {
				console.error('❌ 예상치 못한 응답 구조:', response.data);
				setWishlist([]);
			}
		} catch (error) {
			console.error('❌ 찜 목록 로딩 실패:', error);
			if (error.response?.status === 401) {
				alert('로그인이 만료되었습니다. 다시 로그인해주세요.');
				localStorage.removeItem('access_token');
				localStorage.removeItem('refresh_token');
				localStorage.removeItem('user');
				navigate('/login');
			} else {
				alert('찜 목록을 불러올 수 없습니다.');
				setWishlist([]);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleRemove = async (productId) => {
		try {
			const token = getToken();
			
			await axios.post(
				'https://api.weartoday.kr/api/products/wishlist/toggle/',
				{ product: productId },
				{
					headers: {
						'Authorization': `Bearer ${token}`,
						'Content-Type': 'application/json'
					}
				}
			);

			fetchWishlist();
			window.dispatchEvent(new Event('wishlistUpdated'));
			
		} catch (error) {
			console.error('❌ 삭제 실패:', error);
		}
	};

	const handleClearAll = async () => {
		if (!window.confirm('현재 페이지의 찜 목록을 삭제하시겠습니까?')) {
			return;
		}

		try {
			const token = getToken();
			
			// 현재 페이지 아이템들의 ID만 추출
			const currentPageIds = currentItems.map(item => item.product.id);
			
			// 각 아이템 개별 삭제
			for (const productId of currentPageIds) {
				await axios.post(
					'https://api.weartoday.kr/api/products/wishlist/toggle/',
					{ product: productId },
					{
						headers: {
							'Authorization': `Bearer ${token}`,
							'Content-Type': 'application/json'
						}
					}
				);
			}

			// 삭제 후 목록 새로고침
			await fetchWishlist();
			
			// 현재 페이지가 비었으면 이전 페이지로
			if (currentPage > 1 && currentItems.length === 0) {
				setCurrentPage(currentPage - 1);
			}
			
			window.dispatchEvent(new Event('wishlistUpdated'));
			
		} catch (error) {
			console.error('❌ 삭제 실패:', error);
			alert('삭제에 실패했습니다.');
		}
	};

	const handleAddToCart = (item) => {
		setSelectedProduct(item.product);
		setShowWishPop(true);
	};

	const getImageUrl = (product) => {
		const imageUrl = product.main_images?.find(img => 
			!img.toLowerCase().endsWith('.mp4')
		);
		
		if (imageUrl) {
			// ⭐ /media로 시작하면 도메인 붙이기
			if (imageUrl.startsWith('/media')) {
				return `https://api.weartoday.kr${imageUrl}`;
			}
			return imageUrl;
		}
		
		if (product.image) {
			if (product.image.startsWith('/media')) {
				return `https://api.weartoday.kr${product.image}`;
			}
			return product.image;
		}
		
		return 'https://via.placeholder.com/300?text=No+Image';
	};

	// ⭐ 페이지네이션 계산
	const totalPages = Math.ceil(wishlist.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentItems = wishlist.slice(startIndex, endIndex);

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	if (loading) {
		return (
			<div className="wishlist-page">
				<div className="loading">⏳ 로딩 중...</div>
			</div>
		);
	}

	return (
		<div className="wishlist-page">
			<div className="wishlist-header">
				<h1>찜 목록</h1>
				{wishlist.length > 0 && (
					<button className="clear-all-btn" onClick={handleClearAll}>
						전체 삭제
					</button>
				)}
			</div>

			{wishlist.length === 0 ? (
				<div className="empty-wishlist">
					<div className="empty-icon">💔</div>
					<p>찜한 상품이 없습니다.</p>
					<button onClick={() => navigate('/')}>쇼핑 계속하기</button>
				</div>
			) : (
				<>
					<div className="wishlist-grid">
						{currentItems.map((item) => (
							<div key={item.id} className="wishlist-item">
								<button 
									className="remove-icon"
									onClick={() => handleRemove(item.product.id)}
									title="삭제"
								>
									✕
								</button>
								
								<div 
									className="item-image"
									onClick={() => navigate(`/product/${item.product.id}`)}
								>
									<img 
										src={getImageUrl(item.product)}
										alt={item.product.name}
										onError={(e) => {
											console.log('이미지 로드 실패:', item.product);
											e.target.src = 'https://via.placeholder.com/300?text=No+Image';
										}}
									/>
								</div>

								<div className="item-info">
									<h3 
										className="item-name"
										onClick={() => navigate(`/product/${item.product.id}`)}
									>
										{item.product.name}
									</h3>
									
									<p className="item-price">
										₩{Number(item.product.price).toLocaleString()}
									</p>
								</div>

								<div className="item-actions">
									<button 
										className="btn-cart"
										onClick={() => handleAddToCart(item)}
									>
										🛒 장바구니 담기
									</button>
								</div>
							</div>
						))}
					</div>

					{/* ⭐ 페이지네이션 */}
					{totalPages > 1 && (
						<div className="pagination">
							<button 
								className="page-btn"
								onClick={() => handlePageChange(currentPage - 1)}
								disabled={currentPage === 1}
							>
								‹
							</button>

							{[...Array(totalPages)].map((_, index) => (
								<button
									key={index + 1}
									className={`page-btn ${currentPage === index + 1 ? 'active' : ''}`}
									onClick={() => handlePageChange(index + 1)}
								>
									{index + 1}
								</button>
							))}

							<button 
								className="page-btn"
								onClick={() => handlePageChange(currentPage + 1)}
								disabled={currentPage === totalPages}
							>
								›
							</button>
						</div>
					)}
				</>
			)}

			{showWishPop && (
				<WishPop
					product={selectedProduct}
					onClose={() => {
						setShowWishPop(false);
						setSelectedProduct(null);
					}}
				/>
			)}
		</div>
	);
}

export default Wishlist;